import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, useSpring, useInView, AnimatePresence } from "framer-motion";
import { Zap, Activity, Users, Rocket, Globe, Shield, Brain, Target, ArrowRight, MousePointer2, Box, Layers, GraduationCap, Quote, Anchor, Briefcase, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

// --- Assets & Data ---
const stories = [
  {
    id: 1,
    year: "DEC 2025",
    date: "THE ORIGIN",
    title: "The First Spark",
    content: "While developing the FOZZIL Ecosystem, we realized that the world's culture was trapped in static glass cases. We began drafting the 'Intelligence in Fuels' concept, but the vision quickly expanded. We asked: Can we fuel human curiosity the same way we fuel vehicles?",
    icon: <Zap className="w-full h-full text-blue-500" />,
    color: "#3B82F6", // Blue
    bg: "radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.15) 0%, rgba(0,0,0,1) 70%)"
  },
  {
    id: 2,
    year: "JAN 2026",
    date: "MILESTONE",
    title: "The Institution Milestone",
    content: "We took the theory into the real world. We implemented our first AI Museum project at a leading research institution. We saw firsthand how students and faculty interacted with history when it could finally 'see' and 'talk' back. This was the birth of the S.H.A.D.E. (Spatial Human-Aware Dynamic Exhibit) technology.",
    icon: <GraduationCap className="w-full h-full text-blue-500" />,
    color: "#3B82F6", // Blue
    bg: "radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.15) 0%, rgba(0,0,0,1) 70%)"
  },
  {
    id: 3,
    year: "FEB 2026",
    date: "PIVOT",
    title: "The 'Platform' Pivot",
    content: "The institutional project was a success, but it was tied to a location. Rujai Ananth, our founder, realized that to rule the world of culture, we needed to stop building buildings and start building Platforms. The idea for a 'Museum-in-a-Box' was born. No more massive construction—just pure, plug-and-play intelligence.",
    icon: <Layers className="w-full h-full text-blue-500" />,
    color: "#3B82F6", // Blue
    bg: "radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.15) 0%, rgba(0,0,0,1) 70%)"
  },
  {
    id: 4,
    year: "FEB 2026",
    date: "EVOLUTION",
    title: "The Birth of ARK",
    content: "We needed a vessel that reflected our premium standards. The FOZZIL ARK was designed—a sleek, matte-black edge server that houses T.A.E.L.S. We moved away from 'products' and toward an 'Ecosystem,' unifying all FOZZIL projects under a single FOZZIL ID.",
    icon: <Box className="w-full h-full text-blue-600" />,
    color: "#2563EB", // Blue-600
    bg: "radial-gradient(circle at 50% 50%, rgba(37, 99, 235, 0.15) 0%, rgba(0,0,0,1) 70%)"
  },
  {
    id: 5,
    year: "PRESENT",
    date: "EXPANSION",
    title: "Global Expansion",
    content: "Today, the FOZZIL ARK is ready to depart. We are no longer just an India-based startup; we are a Delaware-incorporated global entity preparing to install ARKs in museums, airports, and galleries across the world.",
    icon: <Globe className="w-full h-full text-blue-500" />,
    color: "#3B82F6", // Blue
    bg: "radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.15) 0%, rgba(0,0,0,1) 70%)"
  }
];

// --- Components ---

const GlitchText = ({ text }) => {
  return (
    <div className="relative inline-block group">
      <span className="relative z-10">{text}</span>
      <span className="absolute top-0 left-0 -z-10 w-full h-full text-red-500 opacity-0 group-hover:opacity-70 group-hover:animate-pulse group-hover:translate-x-[2px]">{text}</span>
      <span className="absolute top-0 left-0 -z-10 w-full h-full text-blue-500 opacity-0 group-hover:opacity-70 group-hover:animate-pulse group-hover:-translate-x-[2px]">{text}</span>
    </div>
  );
};

const CinematicCard = ({ item, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { margin: "-20% 0px -20% 0px", amount: 0.6 });
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1, 0.8]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0]);
  
  return (
    <motion.div 
      ref={ref}
      style={{ scale, opacity }}
      className={`min-h-[80vh] w-full flex items-center justify-center relative perspective-[1000px]`}
    >
      <motion.div 
        animate={{ opacity: isInView ? 1 : 0 }}
        transition={{ duration: 1 }}
        className="absolute inset-0 z-0 pointer-events-none"
        style={{ background: item.bg }}
      />
      
      <div className={`relative z-10 max-w-7xl w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center px-6 lg:px-12 ${index % 2 !== 0 ? 'lg:grid-flow-dense' : ''}`}>
        
        {/* Visual Side */}
        <motion.div
           className={`${index % 2 !== 0 ? 'lg:col-start-2' : ''} flex justify-center order-1 lg:order-none`}
           style={{ x: useTransform(scrollYProgress, [0, 1], [0, index % 2 === 0 ? 50 : -50]) }}
        >
           <div className="relative w-64 h-64 sm:w-80 sm:h-80 md:w-[28rem] md:h-[28rem] perspective-[2000px] group">
              <motion.div 
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="absolute inset-[-15%] border border-dashed border-white/20 rounded-full"
              />
              <motion.div 
                animate={{ rotate: -360 }}
                transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                className="absolute inset-[-8%] border border-white/10 rounded-full"
              />

              <motion.div
                 whileHover={{ rotateX: 10, rotateY: 10 }}
                 className="w-full h-full bg-black/40 backdrop-blur-xl border border-white/10 rounded-[2rem] flex items-center justify-center relative overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.5)]"
              >
                  <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent opacity-50" />
                  <motion.div 
                    className="relative z-10 w-1/3 h-1/3 sm:w-1/2 sm:h-1/2"
                    whileHover={{ scale: 1.1, filter: "brightness(1.5) drop-shadow(0 0 20px currentColor)" }}
                    style={{ color: item.color }}
                  >
                     {item.icon}
                  </motion.div>
                  <div className="absolute top-6 left-6 sm:top-8 sm:left-8 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white/50 rounded-full" />
                  <div className="absolute top-6 right-6 sm:top-8 sm:right-8 w-8 sm:w-12 h-[1px] bg-white/30" />
                  <div className="absolute bottom-6 left-6 sm:bottom-8 sm:left-8 font-mono text-[8px] sm:text-[10px] text-white/30 tracking-[0.2em]">
                    PHASE: {item.id.toString().padStart(3, '0')}
                  </div>
              </motion.div>
           </div>
        </motion.div>

        {/* Content Side */}
        <div className={`${index % 2 !== 0 ? 'lg:col-start-1 text-left lg:text-right' : 'text-left'} space-y-4 sm:space-y-6 order-2 lg:order-none mt-8 lg:mt-0`}>
           <motion.div
             initial={{ opacity: 0, x: -30 }}
             whileInView={{ opacity: 1, x: 0 }}
             transition={{ duration: 0.8, ease: "circOut" }}
           >
              <div className={`flex items-center gap-4 mb-2 ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}>
                 <span className="w-8 lg:w-12 h-[2px]" style={{ backgroundColor: item.color }} />
                 <span className="font-mono text-xs lg:text-sm tracking-[0.4em] uppercase" style={{ color: item.color }}>
                   {item.date}
                 </span>
              </div>
              
              <h2 className={`text-6xl md:text-8xl lg:text-9xl font-black text-white/5 absolute -z-10 -translate-y-12 lg:-translate-y-16 select-none font-sans hidden sm:block ${index % 2 !== 0 ? 'lg:right-0' : 'left-0'}`}>
                {item.year}
              </h2>
              
              <h3 className="text-3xl sm:text-5xl lg:text-7xl font-bold text-white leading-tight mb-4 sm:mb-6 relative">
                 <GlitchText text={item.title} />
              </h3>
              
              <p className={`text-base sm:text-lg lg:text-2xl text-gray-400 font-light leading-relaxed border-white/10 pl-4 sm:pl-6 ${index % 2 !== 0 ? 'lg:border-r-2 lg:border-l-0 lg:pl-0 lg:pr-6 border-l-2' : 'border-l-2'}`}>
                 {item.content}
              </p>
           </motion.div>
        </div>
      </div>
    </motion.div>
  );
}

const StoriesArk = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

  return (
    <div className="bg-black min-h-screen text-white font-sans selection:bg-white/20 overflow-x-hidden">
      
      {/* Progress Bar (Top) */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-indigo-500 to-sky-400 z-50"
        style={{ scaleX }}
      />

      {/* Hero Section */}
      <section className="h-[120vh] relative flex items-center justify-center overflow-hidden">
         <motion.div 
            style={{ y: useTransform(scrollYProgress, [0, 1], [0, -300]) }}
            className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-zinc-900 via-black to-black" 
         />
         
         <div className="z-10 text-center px-4 sm:px-6 max-w-6xl mt-[-10vh] sm:mt-[-20vh]">
            <motion.h1 
               initial={{ opacity: 0, y: 50, scale: 0.95 }}
               animate={{ opacity: 1, y: 0, scale: 1 }}
               transition={{ duration: 1.2, ease: "easeOut" }}
               className="text-5xl sm:text-7xl md:text-8xl lg:text-[10rem] font-bold tracking-tighter text-white mb-6 leading-tight sm:leading-none"
            >
               THE ARK
            </motion.h1>
            
            <motion.p 
               initial={{ opacity: 0 }} 
               animate={{ opacity: 1 }}
               transition={{ delay: 0.5, duration: 1 }}
               className="text-lg sm:text-2xl md:text-3xl lg:text-4xl text-gray-400 font-light max-w-3xl mx-auto leading-relaxed"
            >
               From Vision to Vessel: The Story of Intelligent Culture.
            </motion.p>
         </div>

         <motion.div 
            style={{ 
               opacity: useTransform(scrollYProgress, [0, 0.2], [1, 0]),
               scale: useTransform(scrollYProgress, [0, 0.2], [1, 0.8])
            }}
            className="absolute bottom-20 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4 text-gray-500"
         >
            <span className="text-sm tracking-[0.3em] uppercase">The Odyssey Begins</span>
            <div className="w-px h-16 bg-gradient-to-b from-gray-500 to-transparent" />
         </motion.div>
      </section>

      {/* Timeline */}
      <div className="relative z-10">
         {stories.map((story, index) => (
           <CinematicCard key={story.id} item={story} index={index} />
         ))}
      </div>

      {/* Philosophy Section - The Trinity of Meaning */}
      <section className="py-32 bg-black relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 relative z-10">
              <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  className="text-center mb-24"
              >
                  <h2 className="text-xs sm:text-sm font-bold text-blue-500 tracking-[0.4em] uppercase mb-4">The Philosophy</h2>
                  <h3 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-white tracking-tighter">THE TRINITY OF MEANING</h3>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                  {[
                      {
                          icon: <Anchor className="w-8 h-8" />,
                          title: "The Vessel",
                          subtitle: "Preservation of Diversity",
                          color: "text-blue-500",
                          bgColor: "bg-blue-500/10",
                          hoverBorder: "hover:border-blue-500/30",
                          content: "Historically, an Ark is a massive vessel built to protect life and diversity from destruction. For FOZZIL, the ARK is the vessel that saves history from being forgotten, preserving the 'intelligence' of our civilization and bringing it safely into the future even as physical artifacts decay."
                      },
                      {
                          icon: <Briefcase className="w-8 h-8" />,
                          title: "The Chest",
                          subtitle: "Sacred Knowledge",
                          color: "text-sky-500",
                          bgColor: "bg-sky-500/10",
                          hoverBorder: "hover:border-sky-500/30",
                          content: "Derived from the Latin 'arca', meaning a chest or coffer. Historically, 'The Ark' referred to a sacred chest that held the most important relics. The Cosmic Black ARK is the modern 'sacred chest', housing T.A.E.L.S and the digital twins of the world's most vital artifacts."
                      },
                      {
                          icon: <ShieldCheck className="w-8 h-8" />,
                          title: "The Platform",
                          subtitle: "Refuge & Salvation",
                          color: "text-indigo-500",
                          bgColor: "bg-indigo-500/10",
                          hoverBorder: "hover:border-indigo-500/30",
                          content: "An Ark represents a refuge—a safe place in a changing or chaotic world. In an era where physical culture is struggling to stay relevant, the ARK provides a stable, hardware-light refuge for human heritage, revitalizing the museum industry with plug-and-play intelligence."
                      }
                  ].map((layer, i) => (
                      <motion.div 
                          key={i}
                          initial={{ opacity: 0, y: 50 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.2, duration: 0.8 }}
                          className={`group p-8 rounded-[2.5rem] bg-gradient-to-br from-black via-black to-blue-900/20 border border-blue-500/10 ${layer.hoverBorder} transition-all duration-500 shadow-2xl shadow-blue-900/10`}
                      >
                          <div className={`w-16 h-16 rounded-[32px] ${layer.bgColor} flex items-center justify-center ${layer.color} mb-8 group-hover:scale-110 transition-transform`}>
                              {layer.icon}
                          </div>
                          <h4 className="text-3xl font-bold text-white mb-2">{layer.title}</h4>
                          <p className={`${layer.color} text-sm font-mono tracking-widest uppercase mb-6`}>{layer.subtitle}</p>
                          <p className="text-gray-400 leading-relaxed font-light">
                              {layer.content}
                          </p>
                      </motion.div>
                  ))}
              </div>

              {/* Black Box / Cosmic Black Concept */}
              <motion.div 
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 1.5 }}
                  className="mt-16 sm:mt-32 p-6 sm:p-12 rounded-[2rem] sm:rounded-[3rem] bg-gradient-to-br from-black via-black to-blue-900/20 border border-blue-500/10 flex flex-col md:grid md:grid-cols-[auto_1fr] items-center md:items-start gap-8 sm:gap-12 shadow-2xl shadow-blue-950/20"
              >
                  <div className="w-24 h-24 sm:w-32 sm:h-32 shrink-0 bg-black border border-white/10 rounded-[32px] sm:rounded-[32px] flex items-center justify-center shadow-[0_0_50px_rgba(0,0,0,0.5)]">
                      <img src="/logoicon.png" alt="FOZZIL Icon" className="w-12 h-12 sm:w-16 sm:h-16 object-contain" />
                  </div>
                  <div className="text-center md:text-left">
                      <h5 className="text-xl sm:text-2xl font-bold text-white mb-4">The "Black Box"</h5>
                      <p className="text-gray-400 text-base sm:text-lg lg:text-xl font-light leading-relaxed max-w-4xl">
                          In engineering, a <span className="text-white italic">Black Box</span> is a device where you don't need to see the complex internals to see the incredible results. By combining the sacred meaning of the Ark with the technical purity of a Black Box, we've created a vessel that feels both <span className="text-blue-500 font-medium">Ancient</span> and <span className="text-indigo-400 font-medium">Infinite</span>.
                      </p>
                  </div>
              </motion.div>
          </div>
      </section>

      {/* Founder's Note Section */}
      <section className="min-h-screen flex flex-col items-center justify-center relative perspective-[1000px] bg-zinc-950 py-20 sm:py-32">
         <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(59,130,246,0.05)_0%,_transparent_70%)]" />
         
         <div className="max-w-4xl px-6 relative z-10 text-center">
            <Quote className="w-12 h-12 sm:w-16 sm:h-16 text-blue-500/20 mb-8 sm:mb-12 mx-auto" />
            <motion.div
               initial={{ opacity: 0, y: 30 }}
               whileInView={{ opacity: 1, y: 0 }}
               transition={{ duration: 1 }}
            >
               <h2 className="text-2xl sm:text-3xl md:text-5xl font-medium text-white mb-8 sm:mb-12 leading-tight italic font-serif">
                  "We aren't just making boxes; we are creating the 'Intelligent OS' for physical experiences. Every FOZZIL ARK is a bridge between the ancient world and the AI future."
               </h2>
               <div className="flex flex-col items-center gap-4">
                  <div className="w-8 sm:w-12 h-px bg-blue-500" />
                  <span className="text-lg sm:text-xl font-bold tracking-widest uppercase text-blue-500">Rujai Ananth</span>
                  <span className="text-xs sm:text-sm text-gray-500 tracking-widest uppercase">CEO, FOZZIL</span>
               </div>
            </motion.div>
         </div>
      </section>

      {/* Footer CTA */}
      <section className="h-screen flex items-center justify-center relative perspective-[1000px] bg-black px-6">
         <div className="absolute inset-0 bg-gradient-to-b from-black via-blue-900/10 to-black" />
         
         <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            className="text-center relative z-10"
         >
            <h2 className="text-4xl sm:text-6xl md:text-8xl font-bold mb-6 sm:mb-8 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-white leading-tight">
               JOIN THE ODYSSEY
            </h2>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-400 mb-8 sm:mb-12 max-w-2xl mx-auto leading-relaxed">
               The ARK is departing. Be part of the ecosystem that bridges history and intelligence.
            </p>
            
            <Link 
               to="/fozzil-ark"
               className="group relative inline-flex items-center gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-white text-black text-base sm:text-lg font-bold rounded-full hover:bg-blue-600 hover:text-white border-2 border-white hover:border-blue-600 transition-all duration-300"
            >
               <span className="relative z-10">DISCOVER THE ARK</span>
               <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
         </motion.div>
      </section>

    </div>
  );
};

export default StoriesArk;
